import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { toast, Toaster } from "react-hot-toast";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { FiArrowLeft, FiPlus } from "react-icons/fi";
import { apiCall } from "utils/apiClient";
import {
  getServiceProcessStepsByService,
  getStepSortValue,
  sortServiceProcessSteps,
} from "./serviceProcessApi";

const antIcon = <LoadingOutlined style={{ fontSize: 56 }} spin />;
const MEDIA_BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "";

const resolveMediaUrl = (path) => {
  if (!path) return "";

  const trimmed = String(path).trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const normalizedPath = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  const normalizedBase = MEDIA_BASE_URL.endsWith("/") ? MEDIA_BASE_URL : `${MEDIA_BASE_URL}/`;

  return `${normalizedBase}${normalizedPath}`;
};

const ServiceProcessView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();

  const [service, setService] = useState(location.state?.service || null);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [processLoading, setProcessLoading] = useState(false);
  const [processes, setProcesses] = useState([]);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (!serviceId) return;
      if (service?.id && String(service.id) === String(serviceId) && service?.name) return;

      setServiceLoading(true);

      try {
        const result = await apiCall({
          endpoint: `services/${serviceId}`,
          method: "GET",
        });

        if (result?.status === 200 && result?.data) {
          setService(result.data);
        } else {
          toast.error(result?.message || "Failed to fetch service details");
        }
      } catch (error) {
        console.error("Error fetching service details:", error);
        toast.error("Failed to fetch service details");
      } finally {
        setServiceLoading(false);
      }
    };

    fetchServiceDetails();
  }, [service?.id, service?.name, serviceId]);

  useEffect(() => {
    const fetchProcesses = async () => {
      if (!serviceId) return;

      setProcessLoading(true);

      try {
        const result = await getServiceProcessStepsByService(serviceId);

        if (result?.status === 200) {
          setProcesses(Array.isArray(result?.data) ? sortServiceProcessSteps(result.data) : []);
        } else if (result?.status === 404) {
          setProcesses([]);
        } else {
          setProcesses([]);
          toast.error(result?.message || "Failed to fetch service process steps");
        }
      } catch (error) {
        console.error("Error fetching service process steps:", error);
        toast.error("Failed to fetch service process steps");
      } finally {
        setProcessLoading(false);
      }
    };

    fetchProcesses();
  }, [serviceId]);

  const displayServiceName = service?.name || service?.service_name || "-";
  const totalSteps = processes.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      {serviceLoading ? (
        <div className="flex h-[75vh] w-full items-center justify-center">
          <Spin indicator={antIcon} />
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-[15px] font-semibold text-slate-800">Service Process View</h2>
                <p className="mt-1 text-xs text-slate-500">
                  Step-by-step flow saved for this service.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/service-processes")}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <FiArrowLeft size={14} />
                  Back
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/service-processes/create/${serviceId}`, {
                      state: { service },
                    })
                  }
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700"
                >
                  <FiPlus size={14} />
                  Add Process
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                  Service Name
                </p>
                <p className="mt-1 text-sm font-medium text-slate-800">{displayServiceName}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                  Total Steps
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-800">{totalSteps}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">Process Steps</h3>
            </div>

            {processLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spin />
              </div>
            ) : processes.length > 0 ? (
              <div className="space-y-3 p-4">
                {processes.map((item, index) => {
                  const stepNumber = getStepSortValue(item, index);
                  const iconUrl = resolveMediaUrl(item?.icon);

                  return (
                    <div
                      key={item?.id || `step-${stepNumber}`}
                      className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white shadow-sm">
                          {stepNumber}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-slate-800">
                            {item?.step_title || `Step ${stepNumber}`}
                          </h4>
                          <p className="mt-1 whitespace-pre-line text-xs leading-5 text-slate-600">
                            {item?.step_description || "-"}
                          </p>
                          {(item?.estimated_time || item?.status || item?.icon) && (
                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                              {item?.estimated_time && (
                                <span className="rounded-full bg-white px-2 py-1 shadow-sm">
                                  Time: {item.estimated_time}
                                </span>
                              )}
                              {item?.status && (
                                <span className="rounded-full bg-white px-2 py-1 shadow-sm">
                                  Status: {item.status}
                                </span>
                              )}
                              {item?.icon && (
                                <span className="rounded-full bg-white px-2 py-1 shadow-sm">
                                  Icon: {item.icon}
                                </span>
                              )}
                            </div>
                          )}
                          {item?.icon && iconUrl && (
                            <div className="mt-3">
                              <img
                                src={iconUrl}
                                alt={`${item?.step_title || `Step ${stepNumber}`} icon`}
                                className="h-16 w-16 rounded-lg border border-slate-200 object-cover"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-5 py-10 text-center">
                <p className="text-sm font-medium text-slate-700">No process steps found.</p>
                <p className="mt-1 text-xs text-slate-500">
                  Add process steps to explain this service flow clearly.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default ServiceProcessView;
